<template>
  <div
    :id="id"
    ref="menuItemRef"
    :class="menuItemClass"
  >
    <div
      v-if="!!isPrinting"
      v-tooltip="'Imprimiendo mapa'"
      class="menu-item__icon-printing-container"
    >
      <span
        class="menu-item__icon-printing pi pi-spin pi-cog"
      ></span>
    </div>
    <!-- https://stackoverflow.com/questions/11746619/alt-or-title-attribute-for-i-tag -->
    <a
      :class="linkClass"
      @click="clickItem"
    >
      <span
        :aria-label="title"
        :title="title"
        class="menu-item__container flex align-items-center gap-1"
      >
        <span
          v-show="hasCssIcon"
          :class="classIcon"
          aria-hidden="true"
        >{{ cssIconName }}</span>
        <img
          v-show="!hasCssIcon && !!srcIcon"
          :src="srcIcon"
          class="img-icon mr-2"
        />
        <span
          v-show="!hasCssIcon || props___.sidebar || props___.nestLevel > 0"
          class="align-self-center"
        >{{ title }}</span>
      </span>
    </a>
    <div
      v-if="displayChildren"
      ref="opAnchor"
    >
      <OverlayPanel
        ref="op"
        class="menu-item__child-container"
        :auto-z-index="true"
        :dismissable="opDismissable"
      >
        <MapMenuItem
          v-for="m in props___.menuHierarchyItem.children"
          ref="childTemplateRefs"
          :key="m.name"
          :menu-hierarchy-item="m"
          :sidebar="props___.sidebar"
          :nest-level="props___.nestLevel + 1"
          @click-item="clickChild(m)"
        />
      </OverlayPanel>
    </div>
  </div>
</template>

<script setup>
import {ref, computed, watch} from 'vue';
import {useStore} from 'vuex';
import {useRoute} from 'vue-router';
import OverlayPanel from 'primevue/overlaypanel';

// #region Props, Emits
const route = useRoute();
const childTemplateRefs = ref([]);
const props___ = defineProps({
  menuHierarchyItem: {
    type: Object, // MenuHierarchyItem
    default: null,
  },
  nestLevel: {
    type: Number,
    default: 0,
  },
  sidebar: {
    type: Boolean,
    default: false,
  },
  printing: {
    type: Boolean,
    defaul: false,
  },
});
const menuItemRef = ref();
const opAnchor = ref();
const op = ref();
const store = useStore();
const emit = defineEmits(['click-menu', 'click-item']);
// #endregion

// #region Params, Ref, Reactives, Computed
const opDismissable = computed(() => store.state.app.dismissableOverlay);
const triggerMenu = computed(() => store.state.tutorial.triggerMenu);

const menuId = computed(() => props___.menuHierarchyItem?.id);
const id = computed(() =>
  !!menuId?.value ?
    'menu-item-' + menuId.value :
    null,
);
const itemRoute = computed(() =>
  !!props___.menuHierarchyItem.route ? props___.menuHierarchyItem.route : '',
);
const title = computed(() => {
  return props___.menuHierarchyItem.title;
});
const isPrint = computed(() => {
  if (props___.menuHierarchyItem?.id === 'print') {
    return true;
  }
  return false;
});
const isPrinting = computed(() => {
  return !!isPrint?.value && !!props___.printing;
});
const isMeasureGrid = computed(() => {
  if (props___.menuHierarchyItem?.id?.startsWith('measure') ||
        props___.menuHierarchyItem?.id === 'grid'
  ) {
    return true;
  }
  return false;
});
const menuItemClass = computed(() => {
  const baseClass = 'menu-item';
  let classes = baseClass;
  if (!!props___.menuHierarchyItem?.customClass) {
    classes += ' ' + props___.menuHierarchyItem.customClass;
  }
  if (!!props___.sidebar) {
    classes += ' menu-item--sidebar';
  }
  if (!props___.sidebar) {
    classes += ' menu-item--desktop';
  }
  return classes;
});
const classIcon = computed(() => {
  const baseClass = 'menu-item__icon mx-auto px-1';
  let returnClass = '';

  if (!!props___.menuHierarchyItem?.materialIcon) {
    returnClass = baseClass + ' material-icons';
  }
  if (!!props___.menuHierarchyItem?.icon) {
    returnClass = baseClass + ' pi pi-' + props___.menuHierarchyItem.icon;
  }
  return returnClass;
});
const hasCssIcon = computed(() => {
  return !!classIcon.value;
});
const srcIcon = computed(() => {
  return props___.menuHierarchyItem?.srcIcon;
});
const cssIconName = computed(() => {
  return !!hasCssIcon.value && !!props___.menuHierarchyItem.materialIcon ?
    props___.menuHierarchyItem.materialIcon :
    '';
});
const hasChildren = computed(() => {
  return (
    !!props___.menuHierarchyItem.children &&
    !!props___.menuHierarchyItem.children.length
  );
});
const displayChildren = computed(() => {
  return hasChildren.value && props___.nestLevel <= 1; // Just one level
});
const anyChildActive = computed(() => {
  const childActiveArray = childTemplateRefs.value.map((c) => c.isActive.value);
  return (
    !!childActiveArray &&
    !!childActiveArray.length &&
    childActiveArray.includes(true)
  );
});
const isActive = computed(() => {
  const ownActive =
    !!itemRoute.value &&
    itemRoute.value.name === route.name &&
    route.name !== 'Home' ? true : false;
  return ownActive || anyChildActive.value;
});
const linkClass = computed(() => {
  let baseClass = 'menu-item__link';
  if (!!isPrinting?.value) {
    baseClass = baseClass + ' menu-item__link--printing';
  }
  if (!!isActive?.value && !isMeasureGrid?.value) {
    return baseClass + ' menu-item__link--active';
  }
  if ((!!props___.sidebar && !isActive?.value) || props___.nestLevel > 0) {
    return baseClass + ' menu-item__link--sidebar menu-item__link--blue';
  }
  if (!props___.sidebar && !isActive?.value && props___.nestLevel === 0) {
    return baseClass + ' menu-item__link--top menu-item__link--white';
  }
  return baseClass;
});
// #region Functions
async function clickChild(item, event) {
  if (displayChildren.value) {
    op.value.toggle(event);
  }
  emit('click-item', item);
}
async function clickItem(event) {
  if (displayChildren.value) {
    op.value.toggle(event);
  }
  if (!isPrinting?.value) {
    emit('click-item', props___.menuHierarchyItem);
  }
}
function isItemVisible() {
  if (!!menuItemRef?.value) {
    const computedStyle = window.getComputedStyle(menuItemRef.value);
    const display = computedStyle.getPropertyValue('display');
    return display != 'none';
  }
  return false;
}
// #endregion

// #region Effects, Hooks
watch(triggerMenu, async (newTrigger) => {
  const visible = isItemVisible();
  const newMenuInfo = newTrigger.menuInfo;
  if (newMenuInfo === menuId.value &&
      !!op?.value?.show &&
      !!opAnchor?.value &&
      visible
  ) {
    await store.dispatch('app/setDismissableOverlay', false);
    op.value.show(new MouseEvent('click'), opAnchor.value);
  }
});
// #endregion

// #region Provide, Expose
defineExpose({
  isActive,
});
// #endregion
</script>

<style lang="scss" scoped>
@import "src/styles";
.menu-item {
  position: relative;
  padding: (0.1 * $brand__boxspace--headerheight) $brand__boxspace--extrasmall;
  &--desktop {
    @include max-screen-width($brand__breakpoint--md) {
      display: none;
    }
  }
  &--sidebar {
    @include min-screen-width($brand__breakpoint--md) {
      display: none;
    }
  }
  &__container {
    display: flex;
    & img {
      height: 1rem;
      max-width: 2rem;
    }
  }
  &__icon {
    font-size: $brand__boxspace--headerheight * 0.5;
    user-select: none;

    &--white {
      color: white;
    }
    &--blue {
      color: $brand__color--blue;
    }
    &-printing {
      &-container {
        background-color: $brand__color--blue;
        position: absolute;
        top: 50%;
        left: 50%;
      }
      color: white;
      /* width: 1rem;
         height: 1rem; */
    }
  }
}
a.menu-item__link {
  cursor: pointer;
  /* margin-left: -10px; */
  &--active {
    color: $brand__color--yellow;
  }
  &--blue {
    color: $brand__color--blue;
  }
  &--white {
    color: $brand__color--white;
  }
  &--sidebar {
    &:hover,
    &:active {
      color: $brand__color--yellow;
    }
  }
  &--top {
    &:hover,
    &:active {
      color: $brand__color--yellow;
    }
  }
  &--printing {
    cursor: wait !important;
  }
}
</style>
